/**
 * Map Locations Component
 * Supports both OpenStreetMap (free) and Google Maps
 */

// Global map variables
let currentMap = null;
let currentMarkers = [];

// Initialize OpenStreetMap
function initOpenStreetMap(container, points, customMarkerUrl) {
  container.classList.add('loading');
  container.innerHTML = '<div>Loading map...</div>';

  if (typeof L !== 'undefined' && typeof L.map === 'function') {
    initLeafletMap(container, points, customMarkerUrl);
    return;
  }

  if (!document.querySelector('link[href*="leaflet"]')) {
    const leafletCSS = document.createElement('link');
    leafletCSS.rel = 'stylesheet';
    leafletCSS.href = 'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.css';
    leafletCSS.crossOrigin = '';
    document.head.appendChild(leafletCSS);
  }

  if (!document.querySelector('script[src*="leaflet"]')) {
    const leafletJS = document.createElement('script');
    leafletJS.src = 'https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/leaflet.js';
    leafletJS.crossOrigin = '';
    leafletJS.onload = () => initLeafletMap(container, points, customMarkerUrl);
    leafletJS.onerror = () => {
      container.classList.remove('loading');
      container.innerHTML = '<div>Failed to load map. Please try refreshing the page.</div>';
    };
    document.head.appendChild(leafletJS);
  }
}

function initLeafletMap(container, points, customMarkerUrl) {
  // Remove loading state
  container.classList.remove('loading');
  container.innerHTML = '';

  // Calculate center point
  let centerLat = 0;
  let centerLng = 0;
  let validPoints = points.filter(point => point.latitude && point.longitude);

  if (validPoints.length > 0) {
    centerLat = validPoints.reduce((sum, point) => sum + point.latitude, 0) / validPoints.length;
    centerLng = validPoints.reduce((sum, point) => sum + point.longitude, 0) / validPoints.length;
  } else {
    // Default to a central location if no valid points
    centerLat = 40.7128;
    centerLng = -74.0060;
  }

  // Initialize map
  currentMap = L.map(container).setView([centerLat, centerLng], 10);

  // Add OpenStreetMap tiles
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    maxZoom: 18,
  }).addTo(currentMap);

  // Clear existing markers
  currentMarkers.forEach(marker => currentMap.removeLayer(marker));
  currentMarkers = [];

  // Create custom marker icon if URL is provided, otherwise use default
  let markerIcon = null;
  if (customMarkerUrl && customMarkerUrl.trim() !== '') {
    markerIcon = L.icon({
      iconUrl: customMarkerUrl,
      iconSize: [25, 41], // Size of the icon
      iconAnchor: [12, 41], // Point of the icon which will correspond to marker's location
      popupAnchor: [1, -34], // Point from which the popup should open relative to the iconAnchor
    });
  }

  // Add markers
  validPoints.forEach((point, index) => {
    const markerOptions = markerIcon ? { icon: markerIcon } : {};
    const marker = L.marker([point.latitude, point.longitude], markerOptions)
      .addTo(currentMap)
      .bindPopup(`
        <div class="map-popup">
          <h4>${point.title}</h4>
          ${point.description ? `<p>${point.description}</p>` : ''}
        </div>
      `);

    currentMarkers.push(marker);
  });

  // Fit bounds if there are multiple points
  if (validPoints.length > 1) {
    const group = new L.featureGroup(currentMarkers);
    currentMap.fitBounds(group.getBounds().pad(0.1));
  }
}

// Initialize Google Maps
function initGoogleMaps(container, points, apiKey, customMarkerUrl) {
  // Add loading state
  container.classList.add('loading');
  container.innerHTML = '<div>Loading map...</div>';

  // Check if Google Maps is already loaded
  if (typeof google === 'undefined' || typeof google.maps === 'undefined') {
    // Load Google Maps API
    if (!document.querySelector('script[src*="maps.googleapis.com"]')) {
      const googleScript = document.createElement('script');
      googleScript.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=initGoogleMapCallback`;
      googleScript.async = true;
      googleScript.defer = true;
      document.head.appendChild(googleScript);

      // Store points for callback
      window.mapPoints = points;
      window.mapContainer = container;
      window.mapCustomMarkerUrl = customMarkerUrl;

      // Define callback function
      window.initGoogleMapCallback = function() {
        initGoogleMapInstance(window.mapContainer, window.mapPoints, window.mapCustomMarkerUrl);
      };
    }
  } else {
    initGoogleMapInstance(container, points, customMarkerUrl);
  }
}

function initGoogleMapInstance(container, points, customMarkerUrl) {
  // Remove loading state
  container.classList.remove('loading');
  container.innerHTML = '';

  // Calculate center point
  let centerLat = 0;
  let centerLng = 0;
  let validPoints = points.filter(point => point.latitude && point.longitude);

  if (validPoints.length > 0) {
    centerLat = validPoints.reduce((sum, point) => sum + point.latitude, 0) / validPoints.length;
    centerLng = validPoints.reduce((sum, point) => sum + point.longitude, 0) / validPoints.length;
  } else {
    // Default to a central location if no valid points
    centerLat = 40.7128;
    centerLng = -74.0060;
  }

  // Initialize map
  currentMap = new google.maps.Map(container, {
    zoom: 10,
    center: { lat: centerLat, lng: centerLng },
    mapTypeControl: true,
    streetViewControl: true,
    fullscreenControl: true,
  });

  // Clear existing markers
  currentMarkers.forEach(marker => marker.setMap(null));
  currentMarkers = [];

  // Add markers
  validPoints.forEach((point, index) => {
    const markerOptions = {
      position: { lat: point.latitude, lng: point.longitude },
      map: currentMap,
      title: point.title,
    };

    // Add custom marker icon if URL is provided, otherwise use default with label
    if (customMarkerUrl && customMarkerUrl.trim() !== '') {
      markerOptions.icon = {
        url: customMarkerUrl,
        scaledSize: new google.maps.Size(25, 41), // Size of the icon
        origin: new google.maps.Point(0, 0), // Origin of the icon
        anchor: new google.maps.Point(12, 41) // Anchor point of the icon
      };
    } else {
      markerOptions.label = (index + 1).toString();
    }

    const marker = new google.maps.Marker(markerOptions);

    // Create info window
    const infoWindow = new google.maps.InfoWindow({
      content: `
        <div class="map-popup">
          <h4>${point.title}</h4>
          <p><strong>Address:</strong> ${point.address}</p>
          ${point.description ? `<p>${point.description}</p>` : ''}
        </div>
      `,
    });

    marker.addListener('click', () => {
      infoWindow.open(currentMap, marker);
    });

    currentMarkers.push(marker);
  });

  // Fit bounds if there are multiple points
  if (validPoints.length > 1) {
    const bounds = new google.maps.LatLngBounds();
    validPoints.forEach(point => {
      bounds.extend(new google.maps.LatLng(point.latitude, point.longitude));
    });
    currentMap.fitBounds(bounds);
  }
}

// Geocoding utility for addresses (optional enhancement)
function geocodeAddress(address, callback) {
  // This would require either Google Maps Geocoding API or a geocoding service
  // For now, return null - coordinates should be provided via metaobject
  callback(null);
}

// Export functions for potential external use
window.MapLocations = {
  initOpenStreetMap,
  initGoogleMaps,
  geocodeAddress
};
